import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CampaignListActions, LeadFormActions } from '../../../Services/_actions';
import { userInformationAtom } from '../../../Services/_states';
import {
  filterEnabledCampaigns,
  getCampaignProposalId,
  normalizeLeadFormsList,
} from '../utils/leadFormList';

export default function useCampaignFormOptions({ organisationId, selectedCampaign, selectedForm }) {
  const campaignActions = CampaignListActions();
  const leadFormActions = LeadFormActions();
  const loggedInUser = useRecoilValue(userInformationAtom);

  const [campaignLoading, setCampaignLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [campaignList, setCampaignList] = useState({ pages: 0, list: [], has_next: false });
  const [formList, setFormList] = useState([]);
  const [draft, setDraft] = useState({
    campaign: selectedCampaign || null,
    webForm: selectedForm || null,
  });

  const filterRef = useRef({
    to: loggedInUser?.id,
    include_assigned_by: 0,
    fetch_all: 0,
    next_page: 1,
    search: '',
    organisationId: organisationId || '',
  });

  const fetchFormList = async (campaign) => {
    const campaignId = getCampaignProposalId(campaign);
    setFormList([]);
    if (!campaignId) return;

    setFormLoading(true);
    try {
      const res = await leadFormActions.getFormsByCampaignId(campaignId);
      setFormList(normalizeLeadFormsList(res));
    } finally {
      setFormLoading(false);
    }
  };

  const getCampaigns = async (nextFilter) => {
    filterRef.current = nextFilter;
    setCampaignLoading(true);
    try {
      const res = await campaignActions.getCampaignAssignment(nextFilter);
      setCampaignList({
        ...(res || { pages: 0, has_next: false }),
        list: filterEnabledCampaigns(res?.list),
      });
    } finally {
      setCampaignLoading(false);
    }
  };

  useEffect(() => {
    filterRef.current.to = loggedInUser?.id;
    getCampaigns({ ...filterRef.current, to: loggedInUser?.id });
  }, [loggedInUser?.id]);

  useEffect(() => {
    if (!organisationId) return;
    getCampaigns({
      ...filterRef.current,
      organisationId,
      next_page: 1,
      search: '',
    });
  }, [organisationId]);

  useEffect(() => {
    if (!selectedCampaign && !selectedForm) return;
    setDraft({
      campaign: selectedCampaign || null,
      webForm: selectedForm || null,
    });
    if (selectedCampaign) {
      fetchFormList(selectedCampaign);
    }
  }, [selectedCampaign, selectedForm]);

  const handleSearchCampaign = useCallback((query) => {
    getCampaigns({ ...filterRef.current, search: query || '', next_page: 1 });
  }, []);

  const loadMoreCampaigns = useCallback(async () => {
    const filter = filterRef.current;
    if (!campaignList?.has_next && filter.next_page >= (campaignList?.pages || 0)) return;

    setCampaignLoading(true);
    try {
      const nextFilter = { ...filter, next_page: filter.next_page + 1 };
      filterRef.current = nextFilter;
      const res = await campaignActions.getCampaignAssignment(nextFilter);
      const merged = [
        ...(Array.isArray(campaignList?.list) ? campaignList.list : []),
        ...filterEnabledCampaigns(res?.list),
      ];
      setCampaignList({ ...res, list: merged });
    } finally {
      setCampaignLoading(false);
    }
  }, [campaignList, campaignActions]);

  const handleCampaignChange = (campaign) => {
    setDraft({ campaign: campaign || null, webForm: null });
    fetchFormList(campaign);
  };

  const handleFormChange = (webForm) => {
    setDraft((prev) => ({ ...prev, webForm: webForm || null }));
  };

  return {
    draft,
    formList,
    campaignList,
    campaignLoading,
    formLoading,
    handleSearchCampaign,
    loadMoreCampaigns,
    handleCampaignChange,
    handleFormChange,
  };
}
